import { useCanManageCompany } from "@/hooks/useCanManageCompany";
import { useRef, useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ProductInfoCard } from "@/components/ProductInfoCard";
import { DryingTechnologyField } from "@/components/DryingTechnologyField";
import { ProfessionalAssessment } from "@/components/ProfessionalAssessment";
import { SpecificationsTabs } from "@/components/SpecificationsTabs";
import { AssigneePicker, type CompanyMember } from "@/components/AssigneePicker";
import { fetchCompanyMembers } from "@/lib/companyMembers";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScanLine, Image as ImageIcon, Loader2, Camera, ChevronRight, AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { fetchCurrentCompanyId, useCurrentCompany } from "@/hooks/useCurrentCompany";
import { isWorkflowEnabledForCompany, useWorkflowDefinitions } from "@/hooks/useWorkflowDefinitions";
import boschWashingMachine from "@/assets/bosch-washing-machine.png";
import aegWashingMachine from "@/assets/aeg-l8fb86es-washing-machine.webp";
import bekoDryer from "@/assets/beko-b5t-dryer.webp";
import boschTypeShield from "@/assets/bosch-wae28466sn-typeshield.webp";
import boschWteTypeShield from "@/assets/bosch-wte84105nl-typeshield.png";
import aegTypeShield from "@/assets/aeg-l8fb86es-typeshield.jpg";
import { useEprelSearch, eprelToSpecifications } from "@/hooks/useEprelSearch";
import { useErrorCodes } from "@/hooks/useWorkflowDefaults";
import { useRefurbishmentEstimate, formatMinutes } from "@/hooks/useRefurbishmentEstimate";
import { useHarvestComponentsForBrand } from "@/hooks/useHarvestComponents";
import { useHarvestRecommendations } from "@/hooks/useHarvestRecommendations";
import { useApplianceDryingTechnology } from "@/components/DryingTechnologyField";
import { groupByTechnology, totalRecoveryRange, type PartTechnology } from "@/lib/harvestTechnologyGroups";
import { stablePartId, dedupeById } from "@/lib/harvestSelection";
import { needsImageUpgrade } from "@/lib/applianceImageQuality";
import { getCategoryFallbackImage } from "@/lib/categoryFallbackImage";

import { logSerialChange } from "@/lib/serialAudit";
import { CompleteApplianceDialog } from "@/components/harvest/CompleteApplianceDialog";
import { missingApplianceFields } from "@/lib/applianceDonorCompletion";
import type { DonorAppliance } from "@/lib/sparePartDonor";
import { useSidebar } from "@/components/ui/sidebar";
import { avatarBgClass, displayNameFor, initialsFor } from "@/lib/avatarColor";
import { resolveApplianceCategory } from "@/lib/applianceCategoryDisplay";
import { dryingTechnologyKind, resolveScannedDryingTechnology, type DryingTechnology } from "@/lib/dryerTechnology";


// Detect legacy/pre-EPREL products based on model number patterns
// EPREL database started in 2019, so older model series are not registered
const isLegacyModelPattern = (modelNumber: string): { isLegacy: boolean; reason?: string } => {
  if (!modelNumber) return { isLegacy: false };
  
  const model = modelNumber.toUpperCase().replace(/[\s\-\/]/g, '');
  
  // Bosch legacy washing machine series (pre-2019)
  // WTE = older top-loader/dryer series (~2005-2015)
  // WAE = Classixx/Maxx series (~2008-2014)
  // WLM = Logixx series (~2007-2012)
  // WFO/WFL = older front-loader series (~2005-2010)
  // WVH = washer-dryer combo older series
  if (/^W(TE|AE|LM|FO|FL|VH)\d/.test(model)) {
    return { isLegacy: true, reason: 'Bosch legacy series (pre-2015)' };
  }
  
  // Siemens legacy washing machine series
  // WM = older Siemens series (~2005-2015)
  // WD = washer-dryer older series
  if (/^W[MD]\d{5}/.test(model)) {
    return { isLegacy: true, reason: 'Siemens legacy series (pre-2015)' };
  }
  
  // AEG legacy series
  // L5/L6 = older Lavamat series (~2010-2016)
  // LAV = very old Lavamat series
  if (/^L[456]\d/.test(model) || /^LAV/.test(model)) {
    return { isLegacy: true, reason: 'AEG legacy Lavamat series (pre-2017)' };
  }
  
  // Miele legacy series  
  // W1xxx/W2xxx = older series (pre-2015)
  // WT = older washer-dryer
  if (/^W[12]\d{3}/.test(model) || /^WT\d{3}/.test(model)) {
    return { isLegacy: true, reason: 'Miele legacy series (pre-2015)' };
  }
  
  // Electrolux/Zanussi legacy
  // EW = older Electrolux series
  // ZW = Zanussi older series
  if (/^(EW|ZW)\d{4}/.test(model)) {
    return { isLegacy: true, reason: 'Electrolux/Zanussi legacy series (pre-2015)' };
  }
  
  // Samsung/LG older series (harder to detect, skip for now)
  
  // FD (Fabrikationsdatum) code check - German appliances have manufacturing date codes
  // FD 9xxx = 199x, FD 0xxx-1xxx = 200x-201x (pre-EPREL)
  // This is detected from specifications, not model number
  
  return { isLegacy: false };
};

// Mock data - in a real app this would come from an API
const applianceData: Record<string, {
  name: string;
  modelNumber: string;
  category: string;
  imageUrl?: string;
  difficulty: "Easy" | "Medium" | "Hard";
  timeEstimate: string;
  commonIssues: string[];
  specifications: { label: string; value: string }[];
  eprelReference?: string;
  typeShieldUrl?: string;
  userManualUrl?: string;
}> = {
  "1": {
    name: "BOSCH Serie 6 Washing Machine",
    modelNumber: "WAE28466SN/36",
    category: "Washing Machine",
    imageUrl: boschWashingMachine,
    difficulty: "Medium",
    timeEstimate: "2-4 hours",
    commonIssues: [
      "Door seal leaking",
      "Not draining properly",
      "Excessive vibration during spin",
      "Not heating water",
      "Door won't lock",
      "Error codes E01-E27",
    ],
    // No EPREL reference - product manufactured 2009, predates EPREL database
    specifications: [
      // Column 1 (even indices): Supplier name, Model, Type, Product number, Serial number,
      //   Model placed on the Union market from, Duration of the commercial guarantee
      // Column 2 (odd indices - EPREL data for washing machines): Energy Class, Overall dimensions,
      //   Rated capacity, Water consumption, Programme duration, Weighted energy consumption, Noise
      { label: "Supplier name", value: "Robert Bosch Hausgeräte GmbH" },
      { label: "Energy Class", value: "A+ (legacy rating)" },
      { label: "Model", value: "WAE28466SN" },
      { label: "Overall dimensions (Height x Width x Depth)", value: "85 x 60 x 59 cm" },
      { label: "Appliance Type", value: "WLM40" },
      { label: "Rated capacity", value: "7 kg" },
      { label: "Product number", value: "4130302712130033327" },
      { label: "Water consumption (per cycle, eco 40-60 programme)", value: "N/A (legacy model)" },
      { label: "Serial number", value: "FD 9303 600332" },
      { label: "Programme duration (Rated capacity)", value: "N/A (legacy model)" },
      { label: "Model placed on the Union market from", value: "2009" },
      { label: "Weighted energy consumption (per 100 cycles, eco 40-60 programme)", value: "N/A (legacy model)" },
      { label: "Duration of the commercial guarantee offered by the supplier", value: "" },
      { label: "Airborne acoustical noise emissions (spinning phase)", value: "74 dB" },
    ],
    typeShieldUrl: boschTypeShield,
  },
  "3": {
    name: "AEG 8000 Series Washing Machine",
    modelNumber: "L8FB86ES",
    category: "Washing Machine",
    imageUrl: aegWashingMachine,
    difficulty: "Medium",
    timeEstimate: "2-3 hours",
    commonIssues: [
      "E10: Water supply issue - check faucet and filters",
      "E20: Drainage issue - check drain pump and filter",
      "E40: Door not properly closed",
      "EF0: Power supply unstable",
      "EHO: Water leak protection activated",
      "Spin cycle stops - unbalanced load",
      "Too much foam in drum",
      "Detergent residue in dispenser",
    ],
    userManualUrl: "/documents/aeg-l8fb86es-user-manual.pdf",
    specifications: [
      // Column 1 (even indices): Supplier name, Model, Type, Product number, Serial number,
      //   Model placed on the Union market from, Duration of the commercial guarantee
      // Column 2 (odd indices - EPREL data for washing machines): Energy Class, Overall dimensions,
      //   Rated capacity, Water consumption, Programme duration, Weighted energy consumption, Noise
      { label: "Supplier name", value: "Electrolux Appliances AB" },
      { label: "Energy Class", value: "B" },
      { label: "Model", value: "L8FB86ES" },
      { label: "Overall dimensions (Height x Width x Depth)", value: "85 x 60 x 60 cm" },
      { label: "Appliance Type", value: "FLI576473" },
      { label: "Rated capacity", value: "8 kg" },
      { label: "Product number", value: "914550807 00" },
      { label: "Water consumption (per cycle, eco 40-60 programme)", value: "47 L" },
      { label: "Serial number", value: "816 00280" },
      { label: "Programme duration (Rated capacity)", value: "3h 30min" },
      { label: "Model placed on the Union market from", value: "2016-05-29" },
      { label: "Weighted energy consumption (per 100 cycles, eco 40-60 programme)", value: "54 kWh" },
      { label: "Duration of the commercial guarantee offered by the supplier", value: "24 months" },
      { label: "Airborne acoustical noise emissions (spinning phase)", value: "76 dB (B)" },
    ],
    eprelReference: "293034",
    typeShieldUrl: aegTypeShield,
  },
};

// Helper to generate category-specific specifications structure
const buildCategorySpecifications = (
  category: string,
  scannedAppliance: {
    name: string;
    modelNumber?: string;
    type?: string;
    plateType?: string;
    productNumber?: string;
    serialNumber?: string;
    brand?: string;
    fdNumber?: string;
    zNr?: string;
    plateVersion?: string;
    productionNumber?: string;
  },
  eprelData?: {
    supplierOrTrademark?: string;
    energyClass?: string;
    ratedCapacity?: number;
    waterCons?: number;
    energyConsPer100Cycle?: number;
    energyConsPerCycle?: number;
    noise?: number;
    noiseClass?: string;
    guaranteeDuration?: number;
    dimensionHeight?: number;
    dimensionWidth?: number;
    dimensionDepth?: number;
    programmeDurationRated?: number;
    onMarketStartDate?: number[];
    // Oven-specific
    energyEfficiencyIndex?: number;
    volume?: number;
    heatSource?: string;
    numberOfCavities?: number;
    energyConventional?: number;
    energyForcedAirConvection?: number;
    // Oven cavity data from EPREL API
    cavities?: Array<{
      volume?: number;
      energyClass?: string;
      energyEfficiencyIndex?: number;
      heatSourceType?: string;
      energyConsumptionCycle?: number;
      energyConsumptionCycleFanForced?: number;
    }>;
    // Dishwasher-specific
    ratedCapacityEco?: number;
    waterConsEco?: number;
    programmeDurationEco?: number;
    energyConsEco?: number;
    noiseClassEco?: string;
    // Tumble Dryer-specific
    condensationEfficiency?: number;
    condensationEfficiencyClass?: string;
    programmeDurationDrying?: number;
    weightedEnergyConsDrying?: number;
    noiseDrying?: number;
    noiseClassDrying?: string;
  }
) => {
  const formatDimensions = () => {
    if (eprelData?.dimensionHeight && eprelData?.dimensionWidth && eprelData?.dimensionDepth) {
      return `${eprelData.dimensionHeight} x ${eprelData.dimensionWidth} x ${eprelData.dimensionDepth} cm`;
    }
    return "";
  };

  const formatProgrammeDuration = () => {
    if (eprelData?.programmeDurationRated) {
      const hours = Math.floor(eprelData.programmeDurationRated / 60);
      const mins = eprelData.programmeDurationRated % 60;
      return hours > 0 ? `${hours}h ${mins}min` : `${mins}min`;
    }
    return "";
  };

  const formatProgrammeDurationEco = () => {
    if (eprelData?.programmeDurationEco) {
      const hours = Math.floor(eprelData.programmeDurationEco / 60);
      const mins = eprelData.programmeDurationEco % 60;
      return hours > 0 ? `${hours}h ${mins}min` : `${mins}min`;
    }
    return "";
  };

  const formatProgrammeDurationDrying = () => {
    if (eprelData?.programmeDurationDrying) {
      const hours = Math.floor(eprelData.programmeDurationDrying / 60);
      const mins = eprelData.programmeDurationDrying % 60;
      return hours > 0 ? `${hours}h ${mins}min` : `${mins}min`;
    } else if (eprelData?.programmeDurationRated) {
      const hours = Math.floor(eprelData.programmeDurationRated / 60);
      const mins = eprelData.programmeDurationRated % 60;
      return hours > 0 ? `${hours}h ${mins}min` : `${mins}min`;
    }
    return "";
  };

  const formatMarketDate = () => {
    if (eprelData?.onMarketStartDate && eprelData.onMarketStartDate.length >= 3) {
      return `${eprelData.onMarketStartDate[0]}-${String(eprelData.onMarketStartDate[1]).padStart(2, '0')}-${String(eprelData.onMarketStartDate[2]).padStart(2, '0')}`;
    }
    return "";
  };

  const formatCondensationEfficiency = () => {
    if (eprelData?.condensationEfficiency) {
      let result = `${eprelData.condensationEfficiency}%`;
      if (eprelData.condensationEfficiencyClass) {
        result += ` (${eprelData.condensationEfficiencyClass})`;
      }
      return result;
    } else if (eprelData?.condensationEfficiencyClass) {
      return eprelData.condensationEfficiencyClass;
    }
    return "";
  };

  const formatNoiseDrying = () => {
    if (eprelData?.noiseDrying) {
      let result = `${eprelData.noiseDrying} dB`;
      if (eprelData.noiseClassDrying) {
        result += ` (${eprelData.noiseClassDrying})`;
      }
      return result;
    } else if (eprelData?.noise) {
      return `${eprelData.noise} dB${eprelData.noiseClass ? ` (${eprelData.noiseClass})` : ''}`;
    }
    return "";
  };

  const normalizedCategory = category.toLowerCase();
  
  // Oven-specific specifications
  if (normalizedCategory === 'oven' || normalizedCategory === 'ovens') {
    // Extract cavity data from EPREL API (oven specs are nested in cavities array)
    const cavity = eprelData?.cavities?.[0];
    const volume = eprelData?.volume || cavity?.volume;
    const energyClass = eprelData?.energyClass || cavity?.energyClass || "";
    const energyEfficiencyIndex = eprelData?.energyEfficiencyIndex || cavity?.energyEfficiencyIndex;
    const heatSource = eprelData?.heatSource || (cavity?.heatSourceType ? 
      cavity.heatSourceType.charAt(0) + cavity.heatSourceType.slice(1).toLowerCase() : "");
    const energyConventional = eprelData?.energyConventional || cavity?.energyConsumptionCycle;
    const energyForcedAirConvection = eprelData?.energyForcedAirConvection || cavity?.energyConsumptionCycleFanForced;
    const numberOfCavities = eprelData?.numberOfCavities || eprelData?.cavities?.length;
    
    return [
      // Column 1: Supplier name, Model, Type, Product number, Serial number, Market date, Guarantee
      // Column 2 (Oven): Energy Class, EEI, Volume, Heat source, Cavities, Energy conventional, Energy fan-forced
      { label: "Supplier name", value: eprelData?.supplierOrTrademark || scannedAppliance.name.split(' ')[0] || "" },
      { label: "Energy Class", value: energyClass },
      { label: "Model", value: scannedAppliance.modelNumber || "" },
      { label: "Energy Efficiency Index", value: energyEfficiencyIndex ? `${energyEfficiencyIndex}` : "" },
      { label: "Appliance Type", value: scannedAppliance.type || "" },
      { label: "Type", value: scannedAppliance.plateType || "" },
      { label: "Volume", value: volume ? `${volume} L` : "" },
      { label: "Product number", value: scannedAppliance.productNumber || "" },
      { label: "Heat source", value: heatSource },
      { label: "Serial number", value: scannedAppliance.serialNumber || "" },
      { label: "Number of cavities", value: numberOfCavities ? `${numberOfCavities}` : "" },
      { label: "Model placed on the Union market from", value: formatMarketDate() },
      { label: "Energy consumption per cycle (conventional mode)", value: energyConventional ? `${energyConventional} kWh` : "" },
      { label: "Duration of the commercial guarantee offered by the supplier", value: eprelData?.guaranteeDuration ? `${eprelData.guaranteeDuration} months` : "" },
      { label: "Energy consumption per cycle (fan-forced convection mode)", value: energyForcedAirConvection ? `${energyForcedAirConvection} kWh` : "" },
    ];
  }

  // Dishwasher-specific specifications
  if (normalizedCategory === 'dishwasher' || normalizedCategory === 'dishwashers') {
    return [
      // Column 1: Supplier name, Model, Type, Product number, Serial number, Market date, Guarantee
      // Column 2 (Dishwasher): Energy Class, Dimensions, Rated capacity eco, Water consumption eco,
      //   Programme duration eco, Energy consumption eco, Noise class eco
      { label: "Supplier name", value: eprelData?.supplierOrTrademark || scannedAppliance.name.split(' ')[0] || "" },
      { label: "Energy Class", value: eprelData?.energyClass || "" },
      { label: "Model", value: scannedAppliance.modelNumber || "" },
      { label: "Overall dimensions (Height x Width x Depth)", value: formatDimensions() },
      { label: "Appliance Type", value: scannedAppliance.type || "" },
      { label: "Type", value: scannedAppliance.plateType || "" },
      { label: "Rated capacity for the eco programme", value: eprelData?.ratedCapacityEco ? `${eprelData.ratedCapacityEco} place settings` : (eprelData?.ratedCapacity ? `${eprelData.ratedCapacity} place settings` : "") },
      { label: "Product number", value: scannedAppliance.productNumber || "" },
      { label: "Water consumption (per cycle, based on the eco programme)", value: eprelData?.waterConsEco ? `${eprelData.waterConsEco} L` : (eprelData?.waterCons ? `${eprelData.waterCons} L` : "") },
      { label: "Serial number", value: scannedAppliance.serialNumber || "" },
      { label: "Programme duration for the eco programme", value: formatProgrammeDurationEco() },
      { label: "Model placed on the Union market from", value: formatMarketDate() },
      { label: "Energy consumption (per cycle, based on the eco programme)", value: eprelData?.energyConsEco ? `${eprelData.energyConsEco} kWh` : (eprelData?.energyConsPerCycle ? `${eprelData.energyConsPerCycle} kWh` : "") },
      { label: "Duration of the commercial guarantee offered by the supplier", value: eprelData?.guaranteeDuration ? `${eprelData.guaranteeDuration} months` : "" },
      { label: "Airborne acoustical noise emission class for the eco programme", value: eprelData?.noiseClassEco || eprelData?.noiseClass || "" },
    ];
  }

  // Tumble Dryer-specific specifications
  if (normalizedCategory === 'dryer' || normalizedCategory === 'tumble dryer' || normalizedCategory === 'tumbledryer') {
    return [
      // Column 1: Supplier name, Model, Type, Product number, Serial number, Market date, Guarantee
      // Column 2 (Tumble Dryer): Energy Class, Dimensions, Rated capacity, Condensation efficiency,
      //   Programme duration, Weighted energy consumption, Noise
      { label: "Supplier name", value: eprelData?.supplierOrTrademark || scannedAppliance.name.split(' ')[0] || "" },
      { label: "Energy Class", value: eprelData?.energyClass || "" },
      { label: "Model", value: scannedAppliance.modelNumber || "" },
      { label: "Dimensions", value: formatDimensions() },
      { label: "Appliance Type", value: scannedAppliance.type || "" },
      { label: "Type", value: scannedAppliance.plateType || "" },
      { label: "Rated capacity", value: eprelData?.ratedCapacity ? `${eprelData.ratedCapacity} kg` : "" },
      { label: "Product number", value: scannedAppliance.productNumber || "" },
      { label: "Condensation efficiency", value: formatCondensationEfficiency() },
      { label: "Serial number", value: scannedAppliance.serialNumber || "" },
      { label: "Programme duration - rated capacity", value: formatProgrammeDurationDrying() },
      { label: "Model placed on the Union market from", value: formatMarketDate() },
      { label: "Weighted energy consumption per drying cycle", value: eprelData?.weightedEnergyConsDrying ? `${eprelData.weightedEnergyConsDrying} kWh` : (eprelData?.energyConsPer100Cycle ? `${(eprelData.energyConsPer100Cycle / 100).toFixed(2)} kWh` : "") },
      { label: "Duration of the commercial guarantee offered by the supplier", value: eprelData?.guaranteeDuration ? `${eprelData.guaranteeDuration} months` : "" },
      { label: "Acoustical airborne noise emissions", value: formatNoiseDrying() },
    ];
  }

  // Default: Washing Machine specifications
  return [
    // Column 1: Supplier name, Model, Type, Product number, Serial number, Market date, Guarantee
    // Column 2 (Washing Machine): Energy Class, Dimensions, Rated capacity, Water consumption, Programme duration, Weighted energy, Noise
    { label: "Supplier name", value: eprelData?.supplierOrTrademark || scannedAppliance.name.split(' ')[0] || "" },
    { label: "Energy Class", value: eprelData?.energyClass || "" },
    { label: "Model", value: scannedAppliance.modelNumber || "" },
    { label: "Overall dimensions (Height x Width x Depth)", value: formatDimensions() },
    { label: "Appliance Type", value: scannedAppliance.type || "" },
      { label: "Type", value: scannedAppliance.plateType || "" },
    { label: "Rated capacity", value: eprelData?.ratedCapacity ? `${eprelData.ratedCapacity} kg` : "" },
    { label: "Product number", value: scannedAppliance.productNumber || "" },
    { label: "Water consumption (per cycle, eco 40-60 programme)", value: eprelData?.waterCons ? `${eprelData.waterCons} L` : "" },
    { label: "Serial number", value: scannedAppliance.serialNumber || "" },
    { label: "Programme duration (Rated capacity)", value: formatProgrammeDuration() },
    { label: "Model placed on the Union market from", value: formatMarketDate() },
    { label: "Weighted energy consumption (per 100 cycles, eco 40-60 programme)", value: eprelData?.energyConsPer100Cycle ? `${eprelData.energyConsPer100Cycle} kWh` : "" },
    { label: "Duration of the commercial guarantee offered by the supplier", value: eprelData?.guaranteeDuration ? `${eprelData.guaranteeDuration} months` : "" },
    { label: "Airborne acoustical noise emissions (spinning phase)", value: eprelData?.noise ? `${eprelData.noise} dB${eprelData.noiseClass ? ` (${eprelData.noiseClass})` : ''}` : "" },
  ];
};

// BSH brands (Bosch, Siemens, Neff, Gaggenau, Constructa) print an FD number
// and a Z-Nr. on their rating plate — show those next to the other identifiers.
const isBshBrand = (brand?: string | null) =>
  !!brand && /bosch|siemens|neff|gaggenau|constructa/i.test(brand);

// Samsung plates often carry a "Version" field.
const isSamsungBrand = (brand?: string | null) => !!brand && /samsung/i.test(brand);

// Miele (M-Nr.), Samsung (extended model code), AEG/Electrolux (PNC),
// LG (Product Code) and Wisberg print a production number on their plate.
const hasProductionNumber = (brand?: string | null) =>
  !!brand && /miele|samsung|aeg|electrolux|lg\b|wisberg/i.test(brand);

const getCategorySpecifications = (
  category: string,
  scannedAppliance: Parameters<typeof buildCategorySpecifications>[1],
  eprelData?: Parameters<typeof buildCategorySpecifications>[2]
) => {
  const base = buildCategorySpecifications(category, scannedAppliance, eprelData);
  const brand = scannedAppliance.brand || scannedAppliance.name?.split(" ")[0];
  const specs = [...base];
  if (isBshBrand(brand)) {
    specs.push(
      { label: "FD number", value: scannedAppliance.fdNumber || "" },
      { label: "Z-Nr.", value: scannedAppliance.zNr || "" },
    );
  }
  if (isSamsungBrand(brand)) {
    specs.push({ label: "Version", value: scannedAppliance.plateVersion || "" });
  }
  if (hasProductionNumber(brand)) {
    specs.push({ label: "Production Number", value: scannedAppliance.productionNumber || "" });
  }
  return specs;
};

type ApplianceRecord = {
  id: string;
  name: string | null;
  model_number: string;
  brand: string | null;
  category: string | null;
  image_url: string | null;
  type_shield_url: string | null;
  product_number: string | null;
  serial_number: string | null;
  product_type: string | null;
  plate_type: string | null;
  fd_number?: string | null;
  z_nr?: string | null;
  plate_version?: string | null;
  production_number?: string | null;
  eprel_reference: string | null;
  eprel_data: Record<string, unknown> | null;
  manual_url: string | null;
};

const isUuid = (value: string | undefined) =>
  !!value && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

const categoryDisplayName = (category?: string | null) => {
  if (!category) return "Appliance";

  const normalized = category.toLowerCase().trim();
  const map: Record<string, string> = {
    "washing-machine": "Washing Machine",
    "washing machine": "Washing Machine",
    dishwasher: "Dishwasher",
    refrigerator: "Refrigerator",
    oven: "Oven",
    dryer: "Tumble Dryer",
    "tumble dryer": "Tumble Dryer",
    "vacuum-cleaner": "Vacuum Cleaner",
    "vacuum cleaner": "Vacuum Cleaner",
  };

  return map[normalized] || category;
};

const createPlaceholderAppliance = () => ({
  name: "Unknown Appliance",
  modelNumber: "",
  category: "Appliance",
  difficulty: "Medium" as const,
  timeEstimate: "2-3 hours",
  commonIssues: ["No data available - scan product sheet to populate"],
  specifications: getCategorySpecifications("Appliance", { name: "Unknown Appliance", modelNumber: "" }),
  eprelReference: undefined,
  imageUrl: undefined,
  typeShieldUrl: undefined,
  userManualUrl: undefined,
});

const buildApplianceFromDatabase = (dbAppliance: ApplianceRecord) => {
  const name = dbAppliance.name || [dbAppliance.brand, dbAppliance.model_number].filter(Boolean).join(" ") || "Unknown Appliance";
  const category = resolveApplianceCategory({ category: dbAppliance.category, productType: dbAppliance.product_type }) ?? "Appliance";

  return {
    name,
    modelNumber: dbAppliance.model_number,
    category,
    imageUrl: dbAppliance.image_url || undefined,
    difficulty: "Medium" as const,
    timeEstimate: "2-3 hours",
    commonIssues: ["No data available - scan product sheet to populate"],
    specifications: getCategorySpecifications(
      category,
      {
        name,
        modelNumber: dbAppliance.model_number,
        type: dbAppliance.product_type || undefined,
        plateType: dbAppliance.plate_type || undefined,
        productNumber: dbAppliance.product_number || undefined,
        serialNumber: dbAppliance.serial_number || undefined,
        brand: dbAppliance.brand || undefined,
        fdNumber: dbAppliance.fd_number || undefined,
        zNr: dbAppliance.z_nr || undefined,
        plateVersion: dbAppliance.plate_version || undefined,
        productionNumber: dbAppliance.production_number || undefined,
      },
      (dbAppliance.eprel_data as Parameters<typeof getCategorySpecifications>[2]) || undefined
    ),
    eprelReference: dbAppliance.eprel_reference || undefined,
    typeShieldUrl: dbAppliance.type_shield_url || undefined,
    userManualUrl: dbAppliance.manual_url || undefined,
  };
};

// Helper to find appliance by ID or by matching model number from localStorage
const findAppliance = (id: string | undefined) => {
  // Direct ID match
  if (id && applianceData[id]) {
    return applianceData[id];
  }
  
  // Check localStorage for scanned appliances to get model number
  try {
    const stored = localStorage.getItem("recentlyAccessedAppliances");
    if (stored) {
      const appliances = JSON.parse(stored);
      const scannedAppliance = appliances.find((a: { id: string }) => a.id === id);
      if (scannedAppliance) {
        // Normalize model number for comparison (remove spaces, lowercase)
        const normalizeModel = (model: string) => model.toLowerCase().replace(/[\s-]/g, '');
        const scannedModel = normalizeModel(scannedAppliance.modelNumber);
        
        // Find matching appliance data by model number
        const matchingData = Object.values(applianceData).find((data) => {
          const dataModel = normalizeModel(data.modelNumber);
          return dataModel.includes(scannedModel) || 
                 scannedModel.includes(dataModel) ||
                 scannedModel.includes(dataModel.split(" ")[0].toLowerCase());
        });
        
        console.log("Scanned model:", scannedAppliance.modelNumber, "-> normalized:", scannedModel);
        console.log("Matching data found:", matchingData ? matchingData.modelNumber : "none");
        
        if (matchingData && !/washer[\s_-]?dryer/i.test(scannedAppliance.productType || '')) {
          return { ...matchingData, name: scannedAppliance.name, modelNumber: scannedAppliance.modelNumber };
        }
        // Return a base structure with scanned appliance info using new two-column layout
        // Use brand-specific images and type shields when available
        const brand = scannedAppliance.name.split(' ')[0]?.toUpperCase();
        const category = scannedAppliance.category || 'other';
        
        // Use category+brand fallback images when no product image found
        let brandImage = scannedAppliance.imageUrl; // Use product image from search first
        if (!brandImage) {
          if (category === 'washing-machine') {
            brandImage = brand === 'AEG' ? aegWashingMachine : 
                         brand === 'BOSCH' ? boschWashingMachine : undefined;
          } else if (category === 'dryer') {
            brandImage = brand === 'BEKO' ? bekoDryer : undefined;
          }
        }
        
        // Use stored type shield from scan FIRST, then fallback to brand-specific defaults
        const modelLower = scannedAppliance.modelNumber?.toLowerCase().replace(/[\s-]/g, '') || '';
        const storedTypeShield = scannedAppliance.typeShieldUrl;
        
        // Only use fallback if no stored type shield exists
        let brandTypeShield = storedTypeShield;
        if (!brandTypeShield) {
          // Fallback to known type shields for specific models
          if (brand === 'AEG' && modelLower.includes('l8fb86es')) {
            brandTypeShield = aegTypeShield;
          } else if (brand === 'BOSCH' && modelLower.includes('wae28466sn')) {
            brandTypeShield = boschTypeShield;
          } else if (brand === 'BOSCH' && modelLower.includes('wte84105nl')) {
            brandTypeShield = boschWteTypeShield;
          }
        }
        
        console.log('Appliance:', scannedAppliance.modelNumber, 'Category:', category, 'Image:', brandImage ? 'yes' : 'no');
        
        // Format EPREL data if available
        const eprelData = scannedAppliance.eprelData;
        
        // Check if this is an AEG L8FB86ES model to use the uploaded manual
        const isAegL8Model = modelLower.includes('l8fb86es');
        const aegCommonIssues = [
          "E10: Water supply issue - check faucet and filters",
          "E20: Drainage issue - check drain pump and filter",
          "E40: Door not properly closed",
          "EF0: Power supply unstable",
          "EHO: Water leak protection activated",
          "Spin cycle stops - unbalanced load",
          "Too much foam in drum",
          "Detergent residue in dispenser",
        ];
        
        // Get category-specific display name
        const displayCategory = /washer[\s_-]?dryer/i.test(scannedAppliance.productType || '') ? "Washer Dryer" :
                    scannedAppliance.category === "washing-machine" ? "Washing Machine" :
                    scannedAppliance.category === "vacuum-cleaner" ? "Vacuum Cleaner" :
                    scannedAppliance.category === "dishwasher" ? "Dishwasher" :
                    scannedAppliance.category === "refrigerator" ? "Refrigerator" :
                    scannedAppliance.category === "oven" ? "Oven" :
                    scannedAppliance.category === "dryer" ? "Tumble Dryer" :
                    scannedAppliance.category === "washer-dryer" ? "Washer Dryer" : "Other";
        
        return {
          name: scannedAppliance.name,
          modelNumber: scannedAppliance.modelNumber,
          category: displayCategory,
          imageUrl: brandImage,
          difficulty: "Medium" as const,
          timeEstimate: "2-3 hours",
          commonIssues: isAegL8Model ? aegCommonIssues : ["No data available - scan product sheet to populate"],
          specifications: getCategorySpecifications(displayCategory, scannedAppliance, eprelData),
          eprelReference: scannedAppliance.eprelReference,
          typeShieldUrl: brandTypeShield,
          userManualUrl: isAegL8Model ? "/documents/aeg-l8fb86es-user-manual.pdf" : undefined,
        };
      }
    }
  } catch (e) {
    console.error("Error loading appliance from localStorage:", e);
  }
  
  return createPlaceholderAppliance();
};

const ApplianceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { allowed: isAdmin } = useCanManageCompany();
  const initialAppliance = findAppliance(id);
  
  const [isEditMode, setIsEditMode] = useState(false);
  const [appliance, setAppliance] = useState(initialAppliance);
  const [completePrompt, setCompletePrompt] = useState<DonorAppliance | null>(null);
  const completedSerialRef = useRef<string | null>(null);
  const { id: currentCompanyId } = useCurrentCompany();
  const [isLoadingEprel, setIsLoadingEprel] = useState(false);
  const [hasEprelData, setHasEprelData] = useState(!!initialAppliance.eprelReference);
  const [isPreEprel, setIsPreEprel] = useState(false);
  const [onlineSources, setOnlineSources] = useState<{ url: string; title: string; isSupplierSite: boolean }[]>([]);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const { searchByModelAndBrand, searchByEprelReference, searchOnlineProductInfo, isLoading: eprelLoading, error: eprelError } = useEprelSearch();

  useEffect(() => {
    const hydrateApplianceFromDatabase = async () => {
      if (!id || !user || !isUuid(id)) return;
      const hydrateCompanyId = await fetchCurrentCompanyId(user.id);
      if (!hydrateCompanyId) return;

      const { data, error } = await supabase
        .from("appliances")
        .select("id, name, model_number, brand, category, image_url, type_shield_url, product_number, serial_number, product_type, plate_type, fd_number, z_nr, plate_version, production_number, dryer_technology, eprel_reference, eprel_data, manual_url")
        .eq("id", id)
        .eq("user_id", user.id)
        .eq("company_id", hydrateCompanyId)
        .maybeSingle();

      if (error) {
        console.error("Failed to load appliance from database:", error);
        return;
      }

      if (!data) return;

      setAppliance(buildApplianceFromDatabase(data as ApplianceRecord));
      setHasEprelData(!!data.eprel_reference || !!data.eprel_data);
      setOnlineSources([]);

      try {
        const stored = localStorage.getItem("recentlyAccessedAppliances");
        const existing = stored ? JSON.parse(stored) : [];
        const nextEntry = {
          id: data.id,
          name: data.name || [data.brand, data.model_number].filter(Boolean).join(" ") || "Unknown Appliance",
          modelNumber: data.model_number,
          category: (data.category || "other").toLowerCase().replace(/\s+/g, "-"),
          imageUrl: data.image_url,
          typeShieldUrl: data.type_shield_url,
          productNumber: data.product_number,
          serialNumber: data.serial_number,
          type: data.product_type,
          plateType: data.plate_type,
          fdNumber: (data as { fd_number?: string | null }).fd_number,
          zNr: (data as { z_nr?: string | null }).z_nr,
          plateVersion: (data as { plate_version?: string | null }).plate_version,
          productionNumber: (data as { production_number?: string | null }).production_number,
          dryerTechnology: (data as { dryer_technology?: string | null }).dryer_technology,
          eprelReference: data.eprel_reference,
          eprelData: data.eprel_data,
        };

        const updated = [nextEntry, ...existing.filter((entry: { id?: string }) => entry.id !== data.id)].slice(0, 10);
        localStorage.setItem("recentlyAccessedAppliances", JSON.stringify(updated));
      } catch (storageError) {
        console.error("Failed to sync appliance into localStorage:", storageError);
      }
    };

    hydrateApplianceFromDatabase();
  }, [id, user]);

  // Fetch error codes from workflow management based on brand + category
  const detectedBrand = appliance.name.split(' ')[0] || null;
  const { errorCodes, isLoading: isLoadingErrorCodes } = useErrorCodes(detectedBrand, appliance.category);

  // Update commonIssues when error codes are loaded from the database
  useEffect(() => {
    if (errorCodes.length > 0) {
      const dbIssues = errorCodes.map(ec => 
        ec.description ? `${ec.title} — ${ec.description}` : ec.title
      );
      setAppliance(prev => ({ ...prev, commonIssues: dbIssues }));
    }
  }, [errorCodes]);

  // Search online for product info when EPREL is not available
  const fetchOnlineProductInfo = async (modelNumber: string, brand: string, category: string) => {
    console.log('Searching online for product info:', brand, modelNumber);
    try {
      const onlineResult = await searchOnlineProductInfo(modelNumber, brand, category);
      
      if (onlineResult?.success && onlineResult.data) {
        console.log('Online product info found:', onlineResult.data);
        
        // Store sources for display
        setOnlineSources(onlineResult.data.sources || []);
        
        // Merge online specifications with existing ones
        const onlineSpecs = onlineResult.data.specifications || {};
        
        // Map online spec keys to our labels
        const specMapping: Record<string, string> = {
          'capacity': 'Rated capacity',
          'spin': 'Airborne acoustical noise emissions (spinning phase)',
          'energy': 'Energy Class',
          'dimensions': 'Overall dimensions (Height x Width x Depth)',
          'noise': 'Airborne acoustical noise emissions (spinning phase)',
          'water': 'Water consumption (per cycle, eco 40-60 programme)',
        };
        
        setAppliance(prev => {
          const updatedSpecs = prev.specifications.map(spec => {
            // Check if any online spec matches this label
            for (const [key, value] of Object.entries(onlineSpecs)) {
              const mappedLabel = specMapping[key.toLowerCase()] || key;
              if (spec.label.toLowerCase().includes(key.toLowerCase()) || 
                  spec.label === mappedLabel) {
                if (!spec.value || spec.value === "") {
                  return { ...spec, value };
                }
              }
            }
            return spec;
          });
          
          return { ...prev, specifications: updatedSpecs };
        });
        
        // Show supplier site preference in toast
        const supplierSite = onlineResult.data.sources?.find(s => s.isSupplierSite);
        if (supplierSite) {
          toast.success(`Found info from ${supplierSite.title}`, {
            description: 'Data sourced from manufacturer website'
          });
        } else if (onlineResult.data.sources?.length) {
          toast.info(`Found info from ${onlineResult.data.sources.length} online sources`);
        }
      } else {
        console.log('No online product info found');
      }
    } catch (err) {
      console.error('Error fetching online product info:', err);
    }
  };

  // Fetch EPREL data when component mounts
  useEffect(() => {
    const fetchEprelData = async () => {
      // Extract brand from appliance name (first word typically)
      const brand = appliance.name.split(' ')[0];
      const modelNumber = appliance.modelNumber;
      
      // Get additional OCR fields from localStorage for improved search
      let productNumber: string | null = null;
      let productType: string | null = null;
      
      if (id?.startsWith('scanned-')) {
        try {
          const stored = localStorage.getItem('recentlyAccessedAppliances');
          if (stored) {
            const appliances = JSON.parse(stored);
            const scannedAppliance = appliances.find((a: { id: string }) => a.id === id);
            if (scannedAppliance) {
              productNumber = scannedAppliance.productNumber || null;
              productType = scannedAppliance.type || null;
            }
          }
        } catch (e) {
          console.error('Error getting additional fields from localStorage:', e);
        }
      }
      
      // Wait for the appliance to be hydrated (e.g. from DB) before searching EPREL.
      // Without this guard the effect fires with the placeholder appliance and sends
      // brand="Unknown"/modelNumber="" to EPREL, which always returns "pre-EPREL".
      if (!modelNumber || modelNumber.trim() === "") return;
      if (!brand || brand === "Unknown") return;

      // Get EPREL reference from appliance data if available
      const eprelRef = id && applianceData[id]?.eprelReference;

      // Skip EPREL fetch for known legacy products (hardcoded entries without reference)
      if (!eprelRef && id && applianceData[id]) {
        console.log('Known legacy product without EPREL reference, marking as pre-EPREL');
        setIsPreEprel(true);
        // Try online search for legacy products
        await fetchOnlineProductInfo(modelNumber, brand, appliance.category);
        return;
      }

      // Check if this is a legacy model based on model number pattern
      const legacyCheck = isLegacyModelPattern(modelNumber);
      if (legacyCheck.isLegacy) {
        console.log(`Legacy model detected: ${modelNumber} - ${legacyCheck.reason}`);
        setIsPreEprel(true);
        toast.info(`Pre-EPREL product detected: ${legacyCheck.reason}`);
        // Try online search for legacy products
        await fetchOnlineProductInfo(modelNumber, brand, appliance.category);
        return;
      }

      setIsLoadingEprel(true);
      try {
        let result;
        
        // Use specific EPREL reference if available
        if (eprelRef) {
          result = await searchByEprelReference(eprelRef, appliance.category);
        } else {
          // Use improved search: full model → partial model → product number → brand fallback
          result = await searchByModelAndBrand(modelNumber, brand, appliance.category, {
            productNumber,
            productType,
          });
        }
        
        if (result?.success && result.data) {
          console.log('EPREL data found:', result.data, 'source:', result.source, 'matchType:', result.matchType, 'productGroup:', result.productGroup);
          
          // Check if EPREL returned a different category than what we have
          let detectedCategory = result.detectedCategory;
          // Paired capacities from the shield establish Washer Dryer, regardless of EPREL's category.
          if (appliance.category === "Washer Dryer") detectedCategory = undefined;
          if (id?.startsWith("scanned-")) {
            try {
              const stored = JSON.parse(localStorage.getItem("recentlyAccessedAppliances") || "[]");
              const updated = stored.map((entry: { id: string; dryerTechnology?: DryingTechnology | null }) => entry.id === id ? {
                ...entry,
                eprelClassification: result.classification,
                dryerTechnology: resolveScannedDryingTechnology(entry.dryerTechnology, result.classification?.dryerTechnology),
              } : entry);
              localStorage.setItem("recentlyAccessedAppliances", JSON.stringify(updated));
            } catch (error) {
              console.error("Failed to retain scanned drying technology:", error);
            }
          }
          let updatedCategory = appliance.category;
          
          if (detectedCategory && detectedCategory !== appliance.category.toLowerCase().replace(' ', '-')) {
            console.log('Category updated from EPREL:', appliance.category, '->', detectedCategory);
            // Map detected category to display name
            const categoryDisplayNames: Record<string, string> = {
              'washing-machine': 'Washing Machine',
              'dishwasher': 'Dishwasher',
              'refrigerator': 'Refrigerator',
              'oven': 'Oven',
              'dryer': 'Tumble Dryer',
              'vacuum-cleaner': 'Vacuum Cleaner',
            };
            updatedCategory = categoryDisplayNames[detectedCategory] || appliance.category;
            
            // Update localStorage with the correct category
            if (id?.startsWith('scanned-')) {
              try {
                const stored = localStorage.getItem('recentlyAccessedAppliances');
                if (stored) {
                  const appliances = JSON.parse(stored);
                  const updatedAppliances = appliances.map((a: { id: string; category?: string }) => {
                    if (a.id === id) {
                      return { ...a, category: detectedCategory };
                    }
                    return a;
                  });
                  localStorage.setItem('recentlyAccessedAppliances', JSON.stringify(updatedAppliances));
                  console.log('Updated localStorage with corrected category:', detectedCategory);
                }
              } catch (e) {
                console.error('Failed to update localStorage with category:', e);
              }
            }
            
            toast.info(`Category updated: ${updatedCategory}`);
          }
          
          // Merge EPREL specifications with existing ones, using the correct category
          const eprelSpecs = eprelToSpecifications(result.data, updatedCategory);
          
          // If category changed, replace specs entirely with the correct category template
          // Otherwise merge, preferring EPREL data where available
          let finalSpecs: { label: string; value: string }[];
          
          if (detectedCategory && detectedCategory !== appliance.category.toLowerCase().replace(' ', '-')) {
            // Category changed - use EPREL spec template and fill in local data (serial, product number)
            finalSpecs = eprelSpecs.map(eprelSpec => {
              // Preserve locally-known values (serial number, product number) from existing specs
              if (!eprelSpec.value || eprelSpec.value === "") {
                const existingSpec = appliance.specifications.find(s => s.label === eprelSpec.label);
                if (existingSpec?.value) {
                  return existingSpec;
                }
              }
              return eprelSpec;
            });
            // Keep plate identifiers (Type, FD number, Z-Nr., Version, Production
            // Number, serial…) that the EPREL template does not carry.
            const identifierLabels = [
              "Type",
              "FD number",
              "Z-Nr.",
              "Version",
              "Production Number",
              "Serial number",
              "Product number",
            ];
            for (const label of identifierLabels) {
              if (finalSpecs.some((s) => s.label === label)) continue;
              const existing = appliance.specifications.find((s) => s.label === label);
              if (existing?.value) finalSpecs.push(existing);
            }
          } else {
            // Same category - merge as before
            finalSpecs = appliance.specifications.map(spec => {
              const eprelSpec = eprelSpecs.find(e => e.label === spec.label);
              if (eprelSpec?.value && (!spec.value || spec.value === "")) {
                return eprelSpec;
              }
              return spec;
            });
          }

          setAppliance(prev => ({
            ...prev,
            category: updatedCategory,
            specifications: finalSpecs,
          }));
          
          setHasEprelData(true);
          setIsPreEprel(false);
          toast.success(`EPREL data loaded for ${brand} (${result.source})`);
        } else if (result?.isPreEprel) {
          // Explicitly marked as pre-EPREL by the API
          console.log('Product identified as pre-EPREL:', modelNumber);
          setIsPreEprel(true);
          setHasEprelData(false);
          // Try online search as fallback
          await fetchOnlineProductInfo(modelNumber, brand, appliance.category);
        } else {
          // No data found
          console.log('No EPREL data found for:', modelNumber);
          setIsPreEprel(true);
          setHasEprelData(false);
          // Try online search as fallback
          await fetchOnlineProductInfo(modelNumber, brand, appliance.category);
        }
      } catch (err) {
        console.error('Error fetching EPREL data:', err);
        // On error, assume pre-EPREL and try online search
        setIsPreEprel(true);
        await fetchOnlineProductInfo(modelNumber, brand, appliance.category);
      } finally {
        setIsLoadingEprel(false);
      }
    };

    fetchEprelData();
  }, [id, appliance.modelNumber, appliance.name]); // Re-run after DB hydration populates model/brand

  // Fetch / refresh product image when current one fails the quality bar
  useEffect(() => {
    const fetchProductImage = async () => {
      // Only re-fetch if current image fails the strict ≥75%-coverage rule
      if (!needsImageUpgrade(appliance.imageUrl)) return;

      const brand = appliance.name.split(' ')[0];
      const modelNumber = appliance.modelNumber;

      if (!modelNumber?.trim()) return;
      
      setIsLoadingImage(true);
      
      // Set a 5-second timeout for the loading indicator
      const loadingTimeout = setTimeout(() => {
        setIsLoadingImage(false);
      }, 5000);
      
      try {
        console.log('Fetching product image for:', brand, modelNumber);
        const { data, error } = await supabase.functions.invoke('product-image-search', {
          body: { 
            modelNumber, 
            brand,
            productType: appliance.category
          }
        });
        
        // Clear the loading timeout since we got a response
        clearTimeout(loadingTimeout);
        setIsLoadingImage(false);
        
        if (error) {
          console.error('Product image search error:', error);
          return;
        }
        
        if (data?.success && data?.data?.url) {
          console.log('Product image found:', data.data.url, 'from:', data.data.source);
          setAppliance(prev => ({ ...prev, imageUrl: data.data.url }));

          // Update overview cache so thumbnails appear on the Index page
          try {
            const stored = localStorage.getItem('recentlyAccessedAppliances');
            if (stored) {
              const appliances = JSON.parse(stored);
              const updatedAppliances = appliances.map((a: { id: string; [key: string]: unknown }) => {
                if (a.id === id) {
                  return { ...a, imageUrl: data.data.url };
                }
                return a;
              });
              localStorage.setItem('recentlyAccessedAppliances', JSON.stringify(updatedAppliances));
            }
          } catch (e) {
            console.error('Failed to update localStorage with image:', e);
          }

          // Persist back to the appliances row when this is a real DB record
          const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id || '');
          if (isUuid && user) {
            try {
              await supabase
                .from('appliances')
                .update({ image_url: data.data.url })
                .eq('id', id!)
                .eq('user_id', user.id);
            } catch (e) {
              console.warn('Failed to persist upgraded image to DB:', e);
            }
          }

          toast.success('Product image found', {
            description: `Sourced from ${data.data.source}`
          });
        } else {
          console.log('No product image found');
        }
      } catch (err) {
        console.error('Error fetching product image:', err);
        clearTimeout(loadingTimeout);
        setIsLoadingImage(false);
      }
    };

    // Start image search immediately (runs in parallel with EPREL)
    fetchProductImage();
  }, [id, appliance.imageUrl, appliance.modelNumber, appliance.name, appliance.category]);

  const handleServiceSelect = async (type: "repair" | "refurbish" | "harvest") => {
    if (!user) {
      toast.error("You must be logged in to start a work order");
      return;
    }

    try {
      const companyId = await fetchCurrentCompanyId(user.id);
      if (!companyId) {
        toast.error("You're not assigned to a company yet — contact your admin.");
        return;
      }
      // First, ensure appliance exists in DB
      let applianceId = id || "";

      // Pull extra identification fields from the scanned/localStorage record so
      // serial_number, product_number, EPREL data, etc. are persisted on the
      // appliance row. These are key for the Digital Product Passport.
      const readScannedExtras = () => {
        try {
          const stored = localStorage.getItem("recentlyAccessedAppliances");
          if (!stored) return null;
          const list = JSON.parse(stored) as Array<Record<string, any>>;
          return list.find((a) => a?.id === id) ?? null;
        } catch {
          return null;
        }
      };
      const scanned = readScannedExtras();
      const specSerial = (appliance as any).specifications?.find((s: { label?: string }) => s.label === "Serial number")?.value || null;
      const specProduct = (appliance as any).specifications?.find((s: { label?: string }) => s.label === "Product number" || s.label === "PNC")?.value || null;
      const scannedClassification = scanned?.eprelClassification as
        | import("@/lib/eprelClassification").ApplianceClassification
        | undefined;
      const scannedDryingTechnology = resolveScannedDryingTechnology(scanned?.dryerTechnology, scannedClassification?.dryerTechnology);
      const extraFields: Record<string, unknown> = {
        serial_number: scanned?.serialNumber || specSerial || null,
        product_number: scanned?.productNumber || specProduct || null,
        product_type: scanned?.type || null,
        plate_type: scanned?.plateType || null,
        fd_number: (scanned as { fdNumber?: string } | null)?.fdNumber || null,
        z_nr: (scanned as { zNr?: string } | null)?.zNr || null,
        plate_version: (scanned as { plateVersion?: string } | null)?.plateVersion || null,
        production_number: (scanned as { productionNumber?: string } | null)?.productionNumber || null,
        eprel_reference: (appliance as any).eprelReference || scanned?.eprelReference || null,
        eprel_data: scanned?.eprelData ?? null,
        appliance_class: scannedClassification?.applianceCategory ?? null,
        dryer_technology: scannedDryingTechnology,
        dishwasher_install: scannedClassification?.dishwasherInstall ?? null,
        dishwasher_width: scannedClassification?.dishwasherWidth ?? null,
        dishwasher_capacity: scannedClassification?.dishwasherCapacity ?? null,
        classification_source: scannedClassification ? scannedClassification.classificationSource : null,
      };


      // Check if ID is a valid UUID already in the DB
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(applianceId);

      // Harvesting requires a serial number on the source appliance — it's the
      // anchor for every harvested spare part on the Digital Product Passport.
      // Block the flow and prompt for it if it's not on record yet.
      if (type === "harvest") {
        // FIX-286: the donor appliance needs category, brand, model and serial.
        let row: DonorAppliance | null = null;
        if (isUuid) {
          const { data } = await supabase
            .from("appliances")
            .select("id, category, brand, model_number, serial_number")
            .eq("id", applianceId)
            .maybeSingle();
          row = data;
        }
        const resolvedSerial = (String(extraFields.serial_number ?? "") || completedSerialRef.current || row?.serial_number || "").trim();
        const check: DonorAppliance = row
          ? { ...row, serial_number: resolvedSerial || null }
          // Not saved yet: only the serial can be checked here; the harvest screen checks the rest.
          : { id: applianceId, category: "-", brand: "-", model_number: "-", serial_number: resolvedSerial || null };
        if (missingApplianceFields(check).length > 0) {
          setCompletePrompt(check);
          return;
        }
        extraFields.serial_number = resolvedSerial;
      }


      if (!isUuid) {
        // Non-UUID ID (scanned- prefix or hardcoded like "1", "3") — upsert to DB
        const { data: existingAppliance } = await supabase
          .from("appliances")
          .select("id, serial_number, dryer_technology")
          .eq("model_number", appliance.modelNumber)
          .eq("company_id", companyId)
          .eq("user_id", user.id)
          .maybeSingle();

        if (existingAppliance) {
          applianceId = existingAppliance.id;
          if (!existingAppliance.serial_number && extraFields.serial_number) {
            const fields = { ...extraFields };
            if (!scannedDryingTechnology) delete fields.dryer_technology;
            await supabase.from("appliances").update(fields).eq("id", applianceId).eq("company_id", companyId);
          }
          if (scannedDryingTechnology) {
            const technology = resolveScannedDryingTechnology(scannedDryingTechnology, existingAppliance.dryer_technology as DryingTechnology | null);
            await supabase.from("appliances").update({ dryer_technology: technology }).eq("id", applianceId).eq("company_id", companyId);
          }
        } else {
          const brand = appliance.name.split(" ")[0] || null;
          const { data: newAppliance, error: insertError } = await supabase
            .from("appliances")
            .insert({
              user_id: user.id,
              company_id: companyId,
              model_number: appliance.modelNumber,
              name: appliance.name,
              brand,
              category: appliance.category,
              image_url: appliance.imageUrl || null,
              type_shield_url: appliance.typeShieldUrl || null,
              status: "pending",
              ...extraFields,
            })
            .select("id")
            .single();

          if (insertError) throw insertError;
          applianceId = newAppliance.id;
        }
      } else if (extraFields.serial_number || extraFields.product_number || scannedDryingTechnology) {
        // Existing UUID row — backfill missing serial / product / EPREL fields
        // so harvested parts can always reference the source appliance serial.
        const { data: existing } = await supabase
          .from("appliances")
          .select("serial_number, product_number, dryer_technology")
          .eq("id", applianceId)
          .eq("company_id", companyId)
          .maybeSingle();
        const patch: Record<string, unknown> = {};
        if (!existing?.serial_number && extraFields.serial_number) patch.serial_number = extraFields.serial_number;
        if (!existing?.product_number && extraFields.product_number) patch.product_number = extraFields.product_number;
        if (extraFields.product_type) patch.product_type = extraFields.product_type;
        if (extraFields.plate_type) patch.plate_type = extraFields.plate_type;
        if (extraFields.fd_number) patch.fd_number = extraFields.fd_number;
        if (extraFields.z_nr) patch.z_nr = extraFields.z_nr;
        if (extraFields.plate_version) patch.plate_version = extraFields.plate_version;
        if (extraFields.production_number) patch.production_number = extraFields.production_number;
        if (extraFields.eprel_reference) patch.eprel_reference = extraFields.eprel_reference;
        if (extraFields.eprel_data) patch.eprel_data = extraFields.eprel_data;
        if (extraFields.appliance_class) patch.appliance_class = extraFields.appliance_class;
        if (scannedDryingTechnology) patch.dryer_technology = resolveScannedDryingTechnology(scannedDryingTechnology, existing?.dryer_technology as DryingTechnology | null);
        if (extraFields.dishwasher_install) patch.dishwasher_install = extraFields.dishwasher_install;
        if (extraFields.dishwasher_width) patch.dishwasher_width = extraFields.dishwasher_width;
        if (extraFields.dishwasher_capacity != null) patch.dishwasher_capacity = extraFields.dishwasher_capacity;
        if (extraFields.classification_source) patch.classification_source = extraFields.classification_source;

        if (Object.keys(patch).length > 0) {
          await supabase.from("appliances").update(patch).eq("id", applianceId).eq("company_id", companyId);
        }
      }

      // A freshly-scanned appliance (id starts with "scanned-") represents a new
      // physical unit. Always create a new work order for it instead of resuming
      // a previous one for the same model — that previous order belongs to a
      // different physical appliance and would otherwise show its passport.
      const isFreshScan = typeof id === "string" && id.startsWith("scanned-");

      // Check for existing active work order of same type (only for non-scanned entries)
      const { data: existingWOs } = isFreshScan
        ? { data: [] as { id: string }[] }
        : await supabase
            .from("work_orders")
            .select("id")
            .is("deleted_at", null)
            .eq("appliance_id", applianceId)
            .eq("user_id", user.id)
            .eq("type", type)
            .in("status", ["in_progress", "pending"])
            .limit(1);

      const existingWO = existingWOs && existingWOs.length > 0 ? existingWOs[0] : null;

      let activeWorkOrderId: string | null = existingWO?.id ?? null;

      const workflowKey =
        type === "refurbish" ? "refurbishment" : type === "harvest" ? "harvesting" : "repair";

      const enabled = await isWorkflowEnabledForCompany(workflowKey, companyId);
      if (!enabled) {
        toast.error(`The ${workflowKey} workflow is disabled for your company`);
        return;
      }

      if (existingWO) {
        toast.info(`An active ${type} work order already exists for this appliance`);
      } else {
        const { data: newWO, error: woError } = await supabase
          .from("work_orders")
          .insert({
            appliance_id: applianceId,
            user_id: user.id,
            company_id: companyId,
            type,
            status: "in_progress",
          })
          .select("id")
          .single();

        if (woError) throw woError;
        activeWorkOrderId = newWO?.id ?? null;
        toast.success(`Work order created for ${type}`);
      }

      const woQuery = activeWorkOrderId
        ? `?wo=${activeWorkOrderId}&workOrderId=${activeWorkOrderId}`
        : "";
      navigate(`/appliance/${applianceId}/workflow/${workflowKey}${woQuery}`);
    } catch (err) {
      console.error("Failed to create work order:", err);
      toast.error("Failed to create work order");
    }
  };

  const handleProductNameChange = (name: string) => {
    setAppliance((prev) => ({ ...prev, name }));
    toast.success("Product name updated");
  };

  const handleModelNumberChange = (modelNumber: string) => {
    setAppliance((prev) => ({ ...prev, modelNumber }));
    toast.success("Model number updated");
  };

  const handleCategoryChange = (category: string) => {
    setAppliance((prev) => ({ ...prev, category }));
    toast.success("Category updated");
  };

  const handleSpecificationChange = (label: string, value: string) => {
    // Serial number can only be ADDED by anyone, but CHANGED only by admins.
    if (label === "Serial number") {
      const existing = (appliance.specifications.find((s) => s.label === "Serial number")?.value || "").trim();
      if (existing && existing !== value.trim() && !isAdmin) {
        toast.error("Only admins can change an existing serial number");
        return;
      }
    }
    setAppliance((prev) => ({
      ...prev,
      specifications: prev.specifications.map((spec) =>
        spec.label === label ? { ...spec, value } : spec
      ),
    }));
    
    // Also update localStorage if this is a scanned appliance
    if (id?.startsWith("scanned-")) {
      try {
        const stored = localStorage.getItem("recentlyAccessedAppliances");
        if (stored) {
          const appliances = JSON.parse(stored);
          const updatedAppliances = appliances.map((a: { id: string; [key: string]: unknown }) => {
            if (a.id === id) {
              // Map the label back to the field name
              if (label === "Type") return { ...a, plateType: value };
              if (label === "FD number") return { ...a, fdNumber: value };
              if (label === "Z-Nr.") return { ...a, zNr: value };
              if (label === "Version") return { ...a, plateVersion: value };
              if (label === "Production Number") return { ...a, productionNumber: value };
              if (label === "Appliance Type") return { ...a, type: value };
              if (label === "Product number") return { ...a, productNumber: value };
              if (label === "Serial number") return { ...a, serialNumber: value };
            }
            return a;
          });
          localStorage.setItem("recentlyAccessedAppliances", JSON.stringify(updatedAppliances));
        }
      } catch (e) {
        console.error("Failed to update localStorage:", e);
      }
    }

    // Persist canonical identification fields directly on the DB appliance row
    // (works for UUID-backed appliances). Non-UUID rows fall back to localStorage above.
    const fieldMap: Record<string, string> = {
      "Type": "plate_type",
      "FD number": "fd_number",
      "Z-Nr.": "z_nr",
      "Version": "plate_version",
      "Production Number": "production_number",
      "Appliance Type": "product_type",
      "Product number": "product_number",
      "PNC": "product_number",
      "Serial number": "serial_number",
    };
    const dbField = fieldMap[label];
    if (dbField && id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) {
      const prevSerial = label === "Serial number"
        ? (appliance.specifications.find((s) => s.label === "Serial number")?.value || "")
        : "";
      supabase
        .from("appliances")
        .update({ [dbField]: value })
        .eq("id", id)
        .then(({ error }) => {
          if (error) console.error("Failed to persist spec to DB:", error);
          else if (label === "Serial number") {
            void logSerialChange({ applianceId: id, oldValue: prevSerial, newValue: value });
          }
        });
    }

    toast.success(`${label} updated`);
  };

  return (
    <>
      <ApplianceHubView
        id={id}
        appliance={appliance}
        isPreEprel={isPreEprel}
        isLoadingImage={isLoadingImage}
        isLoadingEprel={isLoadingEprel}
        onServiceSelect={handleServiceSelect}
        onNavigate={navigate}
      />
      <CompleteApplianceDialog
        open={!!completePrompt}
        appliance={completePrompt}
        companyId={currentCompanyId}
        onCancel={() => setCompletePrompt(null)}
        onSaved={(updated) => {
          const before = completePrompt;
          setCompletePrompt(null);
          // FIX-286: reflect the corrected identity in the header/breadcrumb right away.
          const brand = (updated.brand ?? "").trim();
          const model = (updated.model_number ?? "").trim();
          const identityChanged =
            brand !== (before?.brand ?? "").trim() || model !== (before?.model_number ?? "").trim();
          setAppliance((prev: any) => ({
            ...prev,
            ...(identityChanged && (brand || model) ? { name: [brand, model].filter(Boolean).join(" ") } : {}),
            ...(model ? { modelNumber: model } : {}),
            ...(updated.category ? { category: categoryDisplayName(updated.category) } : {}),
          }));
          const serial = (updated.serial_number ?? "").trim();
          if (serial) {
            completedSerialRef.current = serial;
            try {
              const stored = localStorage.getItem("recentlyAccessedAppliances");
              if (stored) {
                const list = JSON.parse(stored) as Array<Record<string, any>>;
                const next = list.map((a) => (a?.id === id ? { ...a, serialNumber: serial } : a));
                localStorage.setItem("recentlyAccessedAppliances", JSON.stringify(next));
              }
            } catch { /* ignore */ }
            setAppliance((prev: any) => ({
              ...prev,
              specifications: (prev.specifications ?? []).map((s: { label: string; value: string }) =>
                s.label === "Serial number" ? { ...s, value: serial } : s,
              ),
            }));
          }
          handleServiceSelect("harvest");
        }}
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// Spec helpers — map "display labels" used in the new hub UI to the underlying
// EPREL spec labels that already populate `appliance.specifications`.
// ---------------------------------------------------------------------------

type Spec = { label: string; value: string };

const SPEC_LABEL_MAP: Array<{ display: string; matchers: (string | RegExp)[]; mono?: boolean }> = [
  { display: "Brand", matchers: ["Supplier name", "Brand"] },
  { display: "Model", matchers: ["Model"], mono: true },
  { display: "Appliance Type", matchers: ["Appliance Type"], mono: true },
  { display: "Type", matchers: ["Type"], mono: true },
  { display: "FD number", matchers: ["FD number"], mono: true },
  { display: "Z-Nr.", matchers: ["Z-Nr."], mono: true },
  { display: "Version", matchers: ["Version"], mono: true },
  { display: "Production Number", matchers: ["Production Number"], mono: true },
  { display: "Product number", matchers: ["Product number", "PNC"], mono: true },
  { display: "Model placed on market", matchers: ["Model placed on the Union market from", /placed on .* market/i] },
  { display: "Supplier warranty", matchers: ["Duration of the commercial guarantee offered by the supplier", /commercial guarantee/i] },
  { display: "Energy class", matchers: ["Energy Class", "Energy class"] },
  { display: "Capacity", matchers: ["Rated capacity", "Rated capacity for the eco programme", "Volume"] },
  { display: "Spin speed", matchers: [/spin speed/i, /maximum spin/i] },
  { display: "Water consumption per cycle", matchers: [/water consumption/i] },
  { display: "Weighted energy consumption", matchers: [/weighted energy consumption/i] },
  { display: "Noise wash", matchers: [/noise.*wash/i, /washing phase/i] },
  { display: "Noise spin", matchers: [/noise.*spin/i, /spinning phase/i] },
  { display: "Dimensions", matchers: [/dimensions/i] },
  { display: "Weight", matchers: [/^weight$/i, /net weight/i] },
];

const findSpec = (specs: Spec[], matchers: (string | RegExp)[]): string => {
  for (const m of matchers) {
    const hit = specs.find((s) =>
      typeof m === "string" ? s.label === m : m.test(s.label),
    );
    if (hit?.value) return hit.value;
  }
  return "";
};

// ---------------------------------------------------------------------------
// Work-order state hook — finds the most recent work order for this appliance.
// ---------------------------------------------------------------------------

type WoState = "none" | "in_progress" | "completed";
interface WoStateResult {
  state: WoState;
  workOrderId: string | null;
  workOrderType: string | null;
  stepNumber: number | null;
  completedAt: string | null;
  assigneeId: string | null;
}

function useApplianceWorkOrderState(applianceId?: string): WoStateResult {
  const [result, setResult] = useState<WoStateResult>({
    state: "none",
    workOrderId: null,
    workOrderType: null,
    stepNumber: null,
    completedAt: null,
    assigneeId: null,
  });

  useEffect(() => {
    let cancelled = false;
    if (!applianceId || !/^[0-9a-f-]{36}$/i.test(applianceId)) return;
    (async () => {
      const { data, error } = await supabase
        .from("work_orders")
        .select("id, type, status, data, completed_at, updated_at, assignee_id")
        .is("deleted_at", null)
        .eq("appliance_id", applianceId)
        .order("updated_at", { ascending: false })
        .limit(1);
      if (cancelled || error || !data || data.length === 0) return;
      const wo = data[0] as {
        id: string;
        type: string;
        status: string | null;
        data: { currentStepIndex?: number } | null;
        completed_at: string | null;
        updated_at: string | null;
        assignee_id: string | null;
      };
      const isCompleted = wo.status === "completed";
      const stepNumber = !isCompleted && wo.data?.currentStepIndex !== undefined
        ? (wo.data.currentStepIndex ?? 0) + 1
        : null;
      setResult({
        state: isCompleted ? "completed" : "in_progress",
        workOrderId: wo.id,
        workOrderType: wo.type,
        stepNumber,
        completedAt: wo.completed_at || wo.updated_at,
        assigneeId: wo.assignee_id,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [applianceId]);

  return result;
}

// ---------------------------------------------------------------------------
// Hub view component
// ---------------------------------------------------------------------------

interface ApplianceHubViewProps {
  id: string | undefined;
  appliance: {
    name: string;
    modelNumber: string;
    category: string;
    imageUrl?: string;
    typeShieldUrl?: string;
    specifications: Spec[];
    commonIssues: string[];
    difficulty: "Easy" | "Medium" | "Hard";
    timeEstimate: string;
  };
  isPreEprel: boolean;
  isLoadingImage: boolean;
  isLoadingEprel: boolean;
  onServiceSelect: (type: "repair" | "refurbish" | "harvest") => void;
  onNavigate: (to: string) => void;
}

const ApplianceHubView = ({
  id,
  appliance,
  isLoadingImage,
  isLoadingEprel,
  onServiceSelect,
  onNavigate,
}: ApplianceHubViewProps) => {
  const [showTypeShield, setShowTypeShield] = useState(false);
  const wo = useApplianceWorkOrderState(id);
  const brand = appliance.name.split(" ")[0] || "";
  const specs = appliance.specifications || [];
  const { state: sidebarState, isMobile: isSidebarMobile } = useSidebar();
  const ctaLeftOffset = isSidebarMobile
    ? "0px"
    : sidebarState === "collapsed"
      ? "var(--sidebar-width-icon)"
      : "var(--sidebar-width)";


  const { data: harvestComponents = [] } = useHarvestComponentsForBrand(
    brand,
    appliance.category,
    appliance.category,
    id,
  );
  // Saved appliances (UUID ids) use the applianceId form, exactly as before.
  // Fresh scans aren't in the DB yet, so pass the scanned attributes plus the
  // active company — the engine applies the same effective-selection filter.
  const { t } = useTranslation("appliance");
  const { id: activeCompanyId, loading: activeCompanyLoading } = useCurrentCompany();
  const isSavedAppliance = isUuid(id);
  const scanTechnology = useMemo(() => {
    if (isSavedAppliance) return null;
    try {
      const list = JSON.parse(localStorage.getItem("recentlyAccessedAppliances") || "[]") as Array<Record<string, any>>;
      const scanned = list.find((a) => a?.id === id);
      const c = scanned?.eprelClassification;
      return scanned?.dryerTechnology || c?.dryerTechnology || c?.dishwasherInstall || c?.dishwasherWidth || null;
    } catch {
      return null;
    }
  }, [id, isSavedAppliance, isLoadingEprel]);
  const harvestRecInput = isSavedAppliance
    ? id
    : { companyId: activeCompanyId, brand: brand || null, category: appliance.category || null, technology: scanTechnology };
  const { data: harvestRecs, isLoading: recsQueryLoading, isError: harvestRecsError } =
    useHarvestRecommendations(harvestRecInput);
  const harvestRecsLoading = !harvestRecsError && (recsQueryLoading || (!isSavedAppliance && activeCompanyLoading));
  void harvestComponents;

  type HarvestPartRow = { id: string; key: string; name: string; original: number; value: number; pct: number; recovery: number; technology: PartTechnology };
  const harvestParts: HarvestPartRow[] = useMemo(() => {
    if (!harvestRecs || harvestRecs.items.length === 0) return [];
    const rawPct = Number(harvestRecs.thresholds?.resale_price_pct) || 0;
    const pct = rawPct <= 1 ? rawPct * 100 : rawPct;
    return dedupeById(harvestRecs.items.map((r) => {
      const original = Number(r.brandPrice) || 0;
      const id = stablePartId(r.partName, r.partCategory, r.technology);
      return {
        id,
        key: id,
        name: r.partName,
        original,
        value: original,
        technology: r.technology ?? "all",
        pct,
        recovery: original * (pct / 100),
      };
    }));
  }, [harvestRecs]);
  const hasPricing = harvestParts.length > 0;
  const { data: harvestDryingInfo } = useApplianceDryingTechnology(isSavedAppliance ? id : null);
  const harvestTechGroups = useMemo(
    () =>
      groupByTechnology(
        harvestParts,
        harvestDryingInfo?.category ?? appliance.category,
        isSavedAppliance ? harvestDryingInfo?.dryer_technology : scanTechnology,
        harvestDryingInfo?.appliance_class,
      ),
    [harvestParts, harvestDryingInfo, appliance.category, isSavedAppliance, scanTechnology],
  );
  const harvestRecoveryLabel = useMemo(() => {
    if (!hasPricing) return "—";
    const pct = harvestParts[0]?.pct ?? 0;
    const [lo, hi] = totalRecoveryRange(harvestTechGroups, pct / 100);
    return harvestTechGroups.unclear ? `€${lo.toFixed(0)}–€${hi.toFixed(0)}` : `€${hi.toFixed(0)}`;
  }, [hasPricing, harvestParts, harvestTechGroups]);

  const renderHarvestPart = (p: HarvestPartRow) => (
                <li key={p.key} className="py-2.5 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-foreground truncate">{p.name}</div>
                    <div className="text-[11px] text-muted-foreground">
                      Orig. €{p.original.toFixed(2)}
                    </div>
                  </div>
                  <div className="text-right shrink-0 flex items-center gap-2">
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground text-[10px] font-medium">
                      {p.pct.toFixed(0)}%
                    </span>
                    <div className="text-sm font-semibold text-foreground">
                      €{p.recovery.toFixed(2)}
                    </div>
                  </div>
                </li>
  );

  const estimatedRecovery = useMemo(
    () => harvestParts.reduce((sum, p) => sum + p.recovery, 0),
    [harvestParts],
  );


  // Assignee resolution: fetch company members lazily when we have an assignee
  const [members, setMembers] = useState<CompanyMember[]>([]);
  useEffect(() => {
    if (!wo.assigneeId) return;
    let cancelled = false;
    (async () => {
      const list = await fetchCompanyMembers();
      if (!cancelled && list.length) setMembers(list);
    })();
    return () => {
      cancelled = true;
    };
  }, [wo.assigneeId]);
  const assignee = members.find((m) => m.id === wo.assigneeId) || null;

  const productNumber = findSpec(specs, ["Product number", "PNC"]);
  const serialNumber = findSpec(specs, ["Serial number"]);
  const manufacturingDate = findSpec(specs, ["Manufacturing date", "Model placed on the Union market from"]);

  // CTA target / label
  const ctaLabel =
    wo.state === "completed"
      ? "View summary"
      : wo.state === "in_progress"
      ? `Continue${wo.stepNumber ? ` — Step ${wo.stepNumber}` : ""}`
      : "Start work order";

  const handleCta = () => {
    if (wo.state === "completed" && id) {
      // For completed harvesting work orders, send the user to HarvestProcess
      // step 5 (the rich summary) instead of the public passport.
      if (wo.workOrderType === "harvest" && wo.workOrderId) {
        onNavigate(`/appliance/${id}/workflow/harvesting?wo=${wo.workOrderId}&workOrderId=${wo.workOrderId}`);
        return;
      }
      onNavigate(`/passport/${id}`);
      return;
    }
    if (wo.state === "in_progress" && wo.workOrderId && id) {
      const key =
        wo.workOrderType === "refurbish"
          ? "refurbishment"
          : wo.workOrderType === "harvest"
          ? "harvesting"
          : wo.workOrderType || "harvesting";
      onNavigate(`/appliance/${id}/workflow/${key}?wo=${wo.workOrderId}&workOrderId=${wo.workOrderId}`);
      return;
    }
    // None — kick off harvesting by default (matches the work orders flow)
    onServiceSelect("harvest");
  };

  const formattedCompletedDate = wo.completedAt
    ? new Date(wo.completedAt).toISOString().slice(0, 10)
    : "";

  // ----- state badge styling -----
  const badge =
    wo.state === "completed"
      ? { cls: "bg-emerald-100 text-emerald-800 border border-emerald-200", label: `Completed · ${formattedCompletedDate}` }
      : wo.state === "in_progress"
      ? { cls: "bg-amber-100 text-amber-900 border border-amber-200", label: `In Progress${wo.stepNumber ? ` · Step ${wo.stepNumber}` : ""}` }
      : { cls: "bg-muted text-muted-foreground border border-border", label: "No work order yet" };

  const fallbackImg = getCategoryFallbackImage(appliance.category);

  return (
    <div className="min-h-screen bg-background">
      <main className="px-6 pt-6 pb-32 max-w-7xl mx-auto space-y-6">
        {/* Breadcrumb */}
        <nav className="text-xs text-muted-foreground">
          <button
            onClick={() => onNavigate("/work-orders")}
            className="hover:text-foreground transition-colors"
          >
            Work Orders
          </button>
          {wo.workOrderId && (
            <>
              <ChevronRight className="inline w-3 h-3 mx-1 align-middle" />
              <span className="font-mono">Work Order ID: {wo.workOrderId.slice(0, 8)}</span>
            </>
          )}
          <ChevronRight className="inline w-3 h-3 mx-1 align-middle" />
          <span className="text-foreground">
            {brand && <span className="font-medium">{brand}</span>}
            {brand && appliance.modelNumber && <span className="mx-1">·</span>}
            <span className="font-mono">{appliance.modelNumber}</span>
          </span>
        </nav>

        {/* Header block */}
        <section className="bg-card rounded-2xl border border-border p-6">
          <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-6 items-start">
            {/* Hero */}
            <div className="w-32 h-32 md:w-40 md:h-40 rounded-xl bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
              {isLoadingImage ? (
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              ) : appliance.imageUrl ? (
                <img src={appliance.imageUrl} alt={appliance.name} className="w-full h-full object-contain" />
              ) : fallbackImg ? (
                <img src={fallbackImg} alt={appliance.name} className="w-3/4 h-3/4 object-contain opacity-70" />
              ) : (
                <ImageIcon className="w-12 h-12 text-muted-foreground" />
              )}
            </div>

            {/* Title block */}
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-1">
                {brand}
                {brand && appliance.category && <span className="mx-1.5">·</span>}
                {/^washer[\s-]?dryer$/i.test(appliance.category || "") ? t("categoryWasherDryer") : appliance.category}
              </div>
              <h1 className="text-2xl md:text-3xl font-semibold text-foreground break-words">
                {appliance.modelNumber || appliance.name}
              </h1>

              <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5 text-sm">
                <div>
                  <dt className="text-xs text-muted-foreground">Product number</dt>
                  <dd className="font-mono text-foreground">{productNumber || "—"}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Serial number</dt>
                  <dd className="flex items-center gap-1.5">
                    <span className="font-mono text-foreground">{serialNumber || "—"}</span>
                    <button
                      type="button"
                      onClick={() => setShowTypeShield(true)}
                      className="h-7 w-7 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      title="View type shield"
                      aria-label="View type shield"
                    >
                      <ScanLine className="w-4 h-4" />
                    </button>
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Manufacturing date</dt>
                  <dd className="text-foreground">{manufacturingDate || "—"}</dd>
                </div>
                <DryingTechnologyField applianceId={id} />
                {!isSavedAppliance && dryingTechnologyKind(appliance.category) && (
                  <div>
                    <dt className="text-xs text-muted-foreground">{t("dryingTechnology.label")}</dt>
                    <dd className="text-sm text-muted-foreground" role={scanTechnology ? undefined : "status"}>
                      {scanTechnology ? t(`dryingTechnology.options.${scanTechnology}`) : t("dryingTechnology.unknownTitle")}
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            {/* State + assignee */}
            <div className="flex flex-col items-start md:items-end gap-2">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${badge.cls}`}>
                {badge.label}
              </span>
              {wo.state === "none" && (
                <p className="text-[13px] text-muted-foreground">
                  Identified from type-shield scan
                </p>
              )}
              {wo.assigneeId && assignee && (
                <div className="flex items-center gap-2 mt-1">
                  <div className={`w-7 h-7 rounded-full ${avatarBgClass(wo.assigneeId)} text-primary-foreground text-[10px] font-semibold flex items-center justify-center`}>
                    {initialsFor(assignee.name, assignee.email)}
                  </div>
                  <span className="text-xs text-foreground">
                    {displayNameFor(assignee.name, assignee.email)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Body split */}
        <section className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Specifications */}
          <div className="lg:col-span-3 bg-card rounded-2xl border border-border p-6">
            <h2 className="text-base font-semibold text-foreground mb-4">Specifications</h2>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
              {SPEC_LABEL_MAP.map((row) => {
                let value = "";
                if (row.display === "Brand") value = brand || findSpec(specs, row.matchers);
                else if (row.display === "Model") value = appliance.modelNumber || findSpec(specs, row.matchers);
                else value = findSpec(specs, row.matchers);
                if (!value) return null;
                return (
                  <div key={row.display} className="flex flex-col gap-0.5">
                    <dt className="text-xs text-muted-foreground">{row.display}</dt>
                    <dd className={`text-foreground ${row.mono ? "font-mono" : ""}`}>{value}</dd>
                  </div>
                );
              })}
            </dl>
          </div>

          {/* Harvestable parts */}
          <div className="lg:col-span-2 bg-card rounded-2xl border border-border p-6">
            <div className="flex items-start justify-between gap-3 mb-1">
              <h2 className="text-base font-semibold text-foreground">Harvestable parts</h2>
              <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                {harvestRecoveryLabel}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              {harvestRecsLoading
                ? "Loading recommendations…"
                : hasPricing
                ? `From the commercial logic for this model — ${harvestParts.length} parts.`
                : "No commercial pricing available for this brand/category yet."}
            </p>
            <ul className="divide-y divide-border">
              {harvestTechGroups.common.map((p) => renderHarvestPart(p))}
            </ul>
            {harvestTechGroups.unclear && (
              <div className="mt-3 rounded-xl border border-warning/40 bg-warning/10 p-3 space-y-2" role="status">
                <div className="flex items-start gap-2 text-sm text-foreground">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-warning" />
                  <span>{t("harvestTechnologyUnclear")}</span>
                </div>
                {isSavedAppliance && <DryingTechnologyField applianceId={id} hideWarning />}
                <ul className="divide-y divide-border">
                  {harvestTechGroups.uncertain.map((p) => renderHarvestPart(p))}
                </ul>
              </div>
            )}

          </div>
        </section>


      </main>

      {/* Fixed bottom CTA bar — pinned to viewport, offset by sidebar width on desktop */}
      <div
        className="fixed bottom-0 right-0 left-0 z-30 bg-card border-t border-border transition-[left] duration-200 ease-linear"
        style={{
          left: ctaLeftOffset,
          boxShadow: "0 -4px 16px -8px hsl(var(--foreground) / 0.08)",
        }}
      >

        <div className="max-w-7xl mx-auto px-6 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-sm text-muted-foreground">
            Estimated recovery:{" "}
            <span className="font-semibold text-foreground">{harvestRecoveryLabel}</span>
          </div>
          <Button size="lg" onClick={handleCta} className="w-full sm:w-auto">
            {ctaLabel}
          </Button>
        </div>
      </div>

      {/* Type shield modal */}
      <Dialog open={showTypeShield} onOpenChange={setShowTypeShield}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Type Shield — {appliance.modelNumber}</DialogTitle>
            {!appliance.typeShieldUrl && (
              <DialogDescription>
                No type shield image available for this product.
              </DialogDescription>
            )}
          </DialogHeader>
          {appliance.typeShieldUrl ? (
            <div className="flex justify-center p-4">
              <img
                src={appliance.typeShieldUrl}
                alt={`Type shield for ${appliance.modelNumber}`}
                className="max-w-full max-h-[60vh] object-contain rounded-lg"
              />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                <Camera className="w-8 h-8 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground mb-4">
                Scan the type shield to capture product specifications.
              </p>
              <Button variant="outline" onClick={() => setShowTypeShield(false)}>
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};


interface ApplianceAssessmentPanelProps {
  difficulty: "Easy" | "Medium" | "Hard";
  fallbackTimeEstimate: string;
  brand: string;
  category: string;
  commonIssues: string[];
  onServiceSelect: (type: "repair" | "refurbish" | "harvest") => void;
}

const SERVICE_TO_WORKFLOW_KEY: Record<"repair" | "refurbish" | "harvest", string> = {
  repair: "repair",
  refurbish: "refurbishment",
  harvest: "harvesting",
};

const ApplianceAssessmentPanel = ({
  difficulty,
  fallbackTimeEstimate,
  brand,
  category,
  commonIssues,
  onServiceSelect,
}: ApplianceAssessmentPanelProps) => {
  const { totalMinutes, isLoading } = useRefurbishmentEstimate(brand, category);
  const liveEstimate = !isLoading && totalMinutes > 0 ? formatMinutes(totalMinutes) : fallbackTimeEstimate;
  const { data: enabledDefs } = useWorkflowDefinitions();
  const enabledKeys = new Set((enabledDefs ?? []).map((d) => d.key));
  const enabledServices = (Object.keys(SERVICE_TO_WORKFLOW_KEY) as Array<"repair" | "refurbish" | "harvest">)
    .filter((s) => enabledKeys.has(SERVICE_TO_WORKFLOW_KEY[s]));
  return (
    <ProfessionalAssessment
      difficulty={difficulty}
      timeEstimate={liveEstimate}
      commonIssues={commonIssues}
      onServiceSelect={onServiceSelect}
      enabledServices={enabledServices}
    />
  );
};


export default ApplianceDetail;
